import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  ZoomIn,
  ZoomOut,
  RotateCw,
  Download,
  X,
  Edit2,
  Square,
  Circle,
  Type,
  Minus,
  ArrowRight,
  Save,
  Trash2,
  RotateCcw
} from 'react-feather';
import './ZoomableImageModal.css';

const ZoomableImageModal = ({
  show,
  onHide,
  imageSrc,
  imageAlt = 'Image',
  onDownload,
  enableEditor = true, // New prop for access control
  onSaveEdited // Callback when edited image is saved
}) => {
  // Viewer state
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Editor state
  const [isEditMode, setIsEditMode] = useState(false);
  const [showEditorToolbar, setShowEditorToolbar] = useState(false);
  const [currentTool, setCurrentTool] = useState('select');
  const [drawColor, setDrawColor] = useState('#FF0000');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);

  // Text input state
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInputPosition, setTextInputPosition] = useState({ x: 0, y: 0 });
  const [textInputValue, setTextInputValue] = useState('');
  const [pendingTextAnnotation, setPendingTextAnnotation] = useState(null);

  // Confirmation modal state
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Custom toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingCanvasRef = useRef(null);
  const textInputRef = useRef(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setScale(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
      setIsEditMode(false);
      setShowEditorToolbar(false);
      setAnnotations([]);
      setHistory([]);
      setHistoryStep(-1);
      setCurrentTool('select');
      setShowTextInput(false);
      setTextInputValue('');
      setPendingTextAnnotation(null);
    }
  }, [show]);

  // Viewer functions
  const handleZoomIn = () => {
    setScale(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleReset = () => {
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (isEditMode) {
      handleEditorMouseDown(e);
    } else if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isEditMode) {
      handleEditorMouseMove(e);
    } else if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = (e) => {
    if (isEditMode) {
      handleEditorMouseUp(e);
    } else {
      setIsDragging(false);
    }
  };

  const handleWheel = (e) => {
    if (!isEditMode) {
      e.preventDefault();
      if (e.deltaY < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    }
  };

  const handleDownload = async () => {
    if (onDownload && imageSrc) {
      onDownload(imageSrc);
    } else {
      try {
        const response = await fetch(imageSrc);
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = imageAlt || 'image';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(blobUrl);
        } else {
          const link = document.createElement('a');
          link.href = imageSrc;
          link.download = imageAlt || 'image';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (error) {
        console.error('Download failed:', error);
        window.open(imageSrc, '_blank');
      }
    }
  };

  // Editor functions
  const toggleEditMode = () => {
    const newEditMode = !isEditMode;
    setIsEditMode(newEditMode);
    setShowEditorToolbar(newEditMode);

    if (newEditMode) {
      // Reset zoom and rotation for better editing experience
      setScale(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });

      // Initialize canvas
      setTimeout(() => {
        initializeCanvas();
      }, 100);
    }
  };

  const initializeCanvas = () => {
    if (!imageRef.current || !canvasRef.current) return;

    const img = imageRef.current;
    const canvas = canvasRef.current;

    // Wait for image to fully load if not already loaded
    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
      console.log('Image not yet loaded, waiting...');
      return;
    }

    const ctx = canvas.getContext('2d');

    // Set canvas size to match image
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    console.log('Canvas initialized:', canvas.width, 'x', canvas.height);

    // Draw existing annotations
    redrawAnnotations();
  };

  const getCanvasCoordinates = (e) => {
    if (!canvasRef.current || !imageRef.current) return { x: 0, y: 0 };

    const canvas = canvasRef.current;
    const img = imageRef.current;
    const rect = img.getBoundingClientRect();

    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;

    // Handle both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    return { x, y };
  };

  const handleEditorMouseDown = (e) => {
    if (currentTool === 'select') return;
    e.preventDefault(); // Prevent default to avoid conflicts with touch

    const coords = getCanvasCoordinates(e);

    setIsDrawing(true);

    const newAnnotation = {
      type: currentTool,
      startX: coords.x,
      startY: coords.y,
      endX: coords.x,
      endY: coords.y,
      color: drawColor,
      strokeWidth: strokeWidth,
      points: currentTool === 'pen' ? [coords] : [],
      text: currentTool === 'text' ? '' : null
    };

    setCurrentAnnotation(newAnnotation);
  };

  const handleEditorTouchStart = (e) => {
    if (currentTool === 'select') return;
    e.preventDefault(); // Prevent scrolling and other default behaviors

    const coords = getCanvasCoordinates(e);

    setIsDrawing(true);

    const newAnnotation = {
      type: currentTool,
      startX: coords.x,
      startY: coords.y,
      endX: coords.x,
      endY: coords.y,
      color: drawColor,
      strokeWidth: strokeWidth,
      points: currentTool === 'pen' ? [coords] : [],
      text: currentTool === 'text' ? '' : null
    };

    setCurrentAnnotation(newAnnotation);
  };

  const handleEditorMouseMove = (e) => {
    if (!isDrawing || !currentAnnotation) {
      return;
    }

    const coords = getCanvasCoordinates(e);

    let updatedAnnotation;
    if (currentTool === 'pen') {
      updatedAnnotation = {
        ...currentAnnotation,
        points: [...currentAnnotation.points, coords]
      };
    } else {
      updatedAnnotation = {
        ...currentAnnotation,
        endX: coords.x,
        endY: coords.y
      };
    }

    setCurrentAnnotation(updatedAnnotation);
    redrawAnnotations([...annotations, updatedAnnotation]);
  };

  const handleEditorTouchMove = (e) => {
    if (!isDrawing || !currentAnnotation) {
      return;
    }
    e.preventDefault(); // Prevent scrolling while drawing

    const coords = getCanvasCoordinates(e);

    let updatedAnnotation;
    if (currentTool === 'pen') {
      updatedAnnotation = {
        ...currentAnnotation,
        points: [...currentAnnotation.points, coords]
      };
    } else {
      updatedAnnotation = {
        ...currentAnnotation,
        endX: coords.x,
        endY: coords.y
      };
    }

    setCurrentAnnotation(updatedAnnotation);
    redrawAnnotations([...annotations, updatedAnnotation]);
  };

  const handleEditorMouseUp = (e) => {
    if (!isDrawing || !currentAnnotation) return;

    setIsDrawing(false);

    if (currentTool === 'text') {
      // Show text input at click position instead of browser prompt
      const rect = imageRef.current.getBoundingClientRect();
      const clientX = e.clientX || (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : 0);
      const clientY = e.clientY || (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : 0);
      setTextInputPosition({
        x: clientX - rect.left,
        y: clientY - rect.top
      });
      setPendingTextAnnotation(currentAnnotation);
      setShowTextInput(true);
      setTextInputValue('');

      // Focus the input after it renders
      setTimeout(() => {
        if (textInputRef.current) {
          textInputRef.current.focus();
        }
      }, 0);
    } else {
      addToHistory([...annotations, currentAnnotation]);
    }

    setCurrentAnnotation(null);
  };

  const handleEditorTouchEnd = (e) => {
    if (!isDrawing || !currentAnnotation) return;
    e.preventDefault();

    setIsDrawing(false);

    if (currentTool === 'text') {
      // Show text input at touch position
      const rect = imageRef.current.getBoundingClientRect();
      const touch = e.changedTouches && e.changedTouches[0];
      if (touch) {
        setTextInputPosition({
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top
        });
        setPendingTextAnnotation(currentAnnotation);
        setShowTextInput(true);
        setTextInputValue('');

        // Focus the input after it renders
        setTimeout(() => {
          if (textInputRef.current) {
            textInputRef.current.focus();
          }
        }, 0);
      }
    } else {
      addToHistory([...annotations, currentAnnotation]);
    }

    setCurrentAnnotation(null);
  };

  const handleTextInputSubmit = () => {
    if (textInputValue.trim() && pendingTextAnnotation) {
      const finalAnnotation = {
        ...pendingTextAnnotation,
        text: textInputValue.trim()
      };
      console.log('Adding text annotation to history:', finalAnnotation);
      addToHistory([...annotations, finalAnnotation]);
    }
    setShowTextInput(false);
    setTextInputValue('');
    setPendingTextAnnotation(null);
  };

  const handleTextInputCancel = () => {
    setShowTextInput(false);
    setTextInputValue('');
    setPendingTextAnnotation(null);
  };

  const handleTextInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextInputSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleTextInputCancel();
    }
  };

  const addToHistory = (newAnnotations) => {
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(newAnnotations);
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
    setAnnotations(newAnnotations);
  };

  const handleUndo = () => {
    if (historyStep > 0) {
      setHistoryStep(historyStep - 1);
      setAnnotations(history[historyStep - 1] || []);
      redrawAnnotations(history[historyStep - 1] || []);
    } else if (historyStep === 0) {
      setHistoryStep(-1);
      setAnnotations([]);
      redrawAnnotations([]);
    }
  };

  const handleClearAll = () => {
    setShowClearConfirm(true);
  };

  const handleConfirmClear = () => {
    setAnnotations([]);
    setHistory([]);
    setHistoryStep(-1);
    redrawAnnotations([]);
    setShowClearConfirm(false);
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };

  const redrawAnnotations = (annotationsToDraw = annotations) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw all annotations
    annotationsToDraw.forEach(annotation => {
      ctx.strokeStyle = annotation.color;
      ctx.fillStyle = annotation.color;
      ctx.lineWidth = annotation.strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      switch (annotation.type) {
        case 'rectangle':
          ctx.strokeRect(
            annotation.startX,
            annotation.startY,
            annotation.endX - annotation.startX,
            annotation.endY - annotation.startY
          );
          break;

        case 'circle':
          const radius = Math.sqrt(
            Math.pow(annotation.endX - annotation.startX, 2) +
            Math.pow(annotation.endY - annotation.startY, 2)
          );
          ctx.beginPath();
          ctx.arc(annotation.startX, annotation.startY, radius, 0, 2 * Math.PI);
          ctx.stroke();
          break;

        case 'line':
          ctx.beginPath();
          ctx.moveTo(annotation.startX, annotation.startY);
          ctx.lineTo(annotation.endX, annotation.endY);
          ctx.stroke();
          break;

        case 'arrow':
          drawArrow(ctx, annotation);
          break;

        case 'pen':
          if (annotation.points.length > 1) {
            ctx.beginPath();
            ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
            annotation.points.forEach(point => {
              ctx.lineTo(point.x, point.y);
            });
            ctx.stroke();
          }
          break;

        case 'text':
          if (annotation.text) {
            ctx.font = `${annotation.strokeWidth * 8}px Arial`;
            ctx.fillText(annotation.text, annotation.startX, annotation.startY);
          }
          break;

        default:
          break;
      }
    });
  };

  const drawArrow = (ctx, annotation) => {
    const { startX, startY, endX, endY } = annotation;
    const headLength = 15 * (annotation.strokeWidth / 3);
    const angle = Math.atan2(endY - startY, endX - startX);

    // Draw line
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    // Draw arrowhead
    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - headLength * Math.cos(angle - Math.PI / 6),
      endY - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - headLength * Math.cos(angle + Math.PI / 6),
      endY - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
  };

  const handleSaveEditedImage = async () => {
    console.log('🔄 handleSaveEditedImage called');

    if (!imageRef.current || !canvasRef.current) {
      console.error('❌ Missing imageRef or canvasRef');
      return;
    }

    if (!onSaveEdited) {
      console.error('❌ onSaveEdited callback is not provided');
      setToastMessage('Save functionality is not available. Please contact support.');
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
      return;
    }

    // Helper function to handle CORS errors
    const handleCorsError = () => {
      console.error('❌ Save failed: Canvas is tainted due to CORS restrictions');

      const errorMessage = 'Cannot save edited image due to CORS restrictions. The image server does not allow cross-origin access. Please configure CORS headers on Azure Blob Storage or contact your administrator.';

      setToastMessage(errorMessage);
      setToastType('error');
      setShowToast(true);

      // Auto-hide after 5 seconds (longer for error messages)
      setTimeout(() => {
        setShowToast(false);
      }, 5000);
    };

    const img = imageRef.current;
    const annotationCanvas = canvasRef.current;
    const originalImageSrc = imageSrc;

    console.log('📸 Image dimensions:', img.naturalWidth, 'x', img.naturalHeight);
    console.log('🎨 Canvas dimensions:', annotationCanvas.width, 'x', annotationCanvas.height);
    console.log('📞 onSaveEdited callback exists:', !!onSaveEdited);
    console.log('🖼️ Original image src:', originalImageSrc);

    // Helper function to try fetching image through proxy
    const fetchImageThroughProxy = async (imageUrl) => {
      try {
        // Check if it's a cross-origin URL
        const imageUrlObj = new URL(imageUrl, window.location.href);
        const isCrossOrigin = imageUrlObj.origin !== window.location.origin;

        if (!isCrossOrigin) {
          console.log('✅ Image is same-origin, no proxy needed');
          return null; // No proxy needed
        }

        // Try to use proxy in development
        if (process.env.NODE_ENV === 'development' && imageUrl.includes('http://')) {
          try {
            const proxyUrl = imageUrl.split('http://')[1];
            const domain = proxyUrl.split('/').shift();
            const path = proxyUrl.split(domain)[1];
            const proxiedUrl = '/image' + path;
            console.log('🔄 Attempting to fetch through proxy:', proxiedUrl);

            const response = await fetch(proxiedUrl);
            if (response.ok) {
              const blob = await response.blob();
              console.log('✅ Successfully fetched image as blob (no CORS):', blob.size, 'bytes');
              return blob;
            }
          } catch (fallbackError) {
            console.warn('⚠️ Fallback fetch also failed:', fallbackError.message);
          }
        }

        return null;
      } catch (error) {
        console.warn('⚠️ Failed to fetch through proxy:', error);
        return null;
      }
    };

    // Declare blobUrlToRevoke in outer scope so it's accessible in catch block
    let blobUrlToRevoke = null;

    try {
      // Try to fetch the image as a blob first (to avoid CORS tainting)
      let imageBlob = null;
      let proxyImage = null;

      // Helper function to fetch image as blob
      const fetchImageAsBlob = async (imageUrl) => {
        try {
          // First try the proxy method
          const proxyBlob = await fetchImageThroughProxy(imageUrl);
          if (proxyBlob) {
            return proxyBlob;
          }

          // If proxy fails, try direct fetch
          const response = await fetch(imageUrl);
          if (response.ok) {
            return await response.blob();
          }
          return null;
        } catch (error) {
          console.warn('Failed to fetch image as blob:', error);
          return null;
        }
      };

      try {
        imageBlob = await fetchImageAsBlob(originalImageSrc);
        if (imageBlob) {
          // Create a new image from the blob
          blobUrlToRevoke = URL.createObjectURL(imageBlob);
          proxyImage = new Image();
          proxyImage.crossOrigin = 'anonymous';

          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Image load timeout'));
            }, 10000); // 10 second timeout
            
            proxyImage.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            proxyImage.onerror = (err) => {
              clearTimeout(timeout);
              reject(err);
            };
            proxyImage.src = blobUrlToRevoke;
          });

          console.log('✅ Proxy image loaded successfully');
        }
      } catch (fetchError) {
        console.warn('⚠️ Failed to fetch image as blob, will try direct method (may cause CORS error):', fetchError);
      }

      // Create a temporary canvas to combine image and annotations
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = img.naturalWidth;
      tempCanvas.height = img.naturalHeight;
      const ctx = tempCanvas.getContext('2d');

      console.log('🖼️ Drawing original image to temp canvas...');
      // Draw the original image (use clean image from blob if available, otherwise use original)
      // If we use the clean image, the canvas won't be tainted
      if (proxyImage) {
        ctx.drawImage(proxyImage, 0, 0);
      } else {
        // Try to draw the original image - this may taint the canvas if CORS is not configured
        try {
          ctx.drawImage(img, 0, 0);
        } catch (drawError) {
          console.error('❌ Failed to draw image to canvas:', drawError);
          throw new Error('Cannot draw image to canvas. This may be due to CORS restrictions.');
        }
      }

      console.log('✏️ Drawing annotations to temp canvas...');
      // Draw annotations on top
      ctx.drawImage(annotationCanvas, 0, 0);

      // Convert to blob
      // Use a wrapper to catch any errors
      const blobCallback = (blob) => {
        console.log('📦 toBlob callback called, blob:', blob ? `exists (${blob.size} bytes)` : 'null');

        // Clean up blob URL if we created one
        if (blobUrlToRevoke) {
          URL.revokeObjectURL(blobUrlToRevoke);
          blobUrlToRevoke = null;
        }

        try {
          if (blob) {
            console.log('✅ Blob created successfully, calling onSaveEdited callback...');
            // Success - call callback if provided
            if (onSaveEdited) {
              console.log('📞 Calling onSaveEdited with blob...');
              try {
                // Call the callback - this should trigger the upload API
                onSaveEdited(blob);
                console.log('✅ onSaveEdited called successfully');
              } catch (callbackError) {
                console.error('❌ Error calling onSaveEdited:', callbackError);
                setToastMessage('Failed to save: ' + (callbackError.message || 'Unknown error'));
                setToastType('error');
                setShowToast(true);
                setTimeout(() => setShowToast(false), 5000);
                return; // Don't show success message if callback failed
              }
            } else {
              console.warn('⚠️ onSaveEdited callback is not provided');
            }

            // Show custom success toast
            setToastMessage('Edited image saved successfully!');
            setToastType('success');
            setShowToast(true);

            // Auto-hide after 3 seconds
            setTimeout(() => {
              setShowToast(false);
            }, 3000);
          } else {
            // Blob is null - canvas is tainted due to CORS
            console.error('❌ Blob is null - canvas is tainted due to CORS');

            // Fallback: Try to export just the annotations canvas (shouldn't be tainted)
            console.log('🔄 Attempting fallback: exporting annotations only...');
            try {
              const annotationsCanvas = document.createElement('canvas');
              annotationsCanvas.width = annotationCanvas.width;
              annotationsCanvas.height = annotationCanvas.height;
              const annotationsCtx = annotationsCanvas.getContext('2d');

              // Draw only the annotations (this shouldn't be tainted)
              annotationsCtx.drawImage(annotationCanvas, 0, 0);

              annotationsCanvas.toBlob((annotationsBlob) => {
                if (annotationsBlob && onSaveEdited) {
                  console.log('✅ Annotations-only blob created, calling onSaveEdited...');
                  try {
                    onSaveEdited(annotationsBlob);
                    setToastMessage('Annotations saved (original image could not be included due to CORS restrictions).');
                    setToastType('warning');
                    setShowToast(true);
                    setTimeout(() => setShowToast(false), 5000);
                  } catch (err) {
                    console.error('❌ Error calling onSaveEdited with annotations:', err);
                    handleCorsError();
                  }
                } else {
                  handleCorsError();
                }
              }, 'image/png');
            } catch (fallbackError) {
              console.error('❌ Fallback also failed:', fallbackError);
              handleCorsError();
            }
          }
        } catch (callbackError) {
          console.error('❌ Error in blob callback:', callbackError);
          handleCorsError();
        }
      };

      // Wrap toBlob call to catch synchronous errors
      try {
        console.log('🔄 Calling toBlob on temp canvas...');
        tempCanvas.toBlob(blobCallback, 'image/png');
        console.log('✅ toBlob called (async)');
      } catch (toBlobError) {
        // toBlob threw synchronously (shouldn't happen, but catch it anyway)
        console.error('❌ toBlob synchronous error:', toBlobError);
        
        // Clean up blob URL if we created one
        if (blobUrlToRevoke) {
          URL.revokeObjectURL(blobUrlToRevoke);
          blobUrlToRevoke = null;
        }
        
        // Try fallback: export annotations only
        console.log('🔄 Attempting fallback: exporting annotations only...');
        try {
          const annotationsCanvas = document.createElement('canvas');
          annotationsCanvas.width = annotationCanvas.width;
          annotationsCanvas.height = annotationCanvas.height;
          const annotationsCtx = annotationsCanvas.getContext('2d');
          
          // Draw only the annotations (this shouldn't be tainted)
          annotationsCtx.drawImage(annotationCanvas, 0, 0);
          
          annotationsCanvas.toBlob((annotationsBlob) => {
            if (annotationsBlob && onSaveEdited) {
              console.log('✅ Annotations-only blob created, calling onSaveEdited...');
              try {
                onSaveEdited(annotationsBlob);
                setToastMessage('Annotations saved (original image could not be included due to CORS restrictions).');
                setToastType('warning');
                setShowToast(true);
                setTimeout(() => setShowToast(false), 5000);
              } catch (err) {
                console.error('❌ Error calling onSaveEdited with annotations:', err);
                handleCorsError();
              }
            } else {
              handleCorsError();
            }
          }, 'image/png');
        } catch (fallbackError) {
          console.error('❌ Fallback also failed:', fallbackError);
          handleCorsError();
        }
      }
    } catch (error) {
      // Catch any other errors during canvas operations
      console.error('❌ Save failed:', error);
      
      // Clean up blob URL if we created one
      if (blobUrlToRevoke) {
        URL.revokeObjectURL(blobUrlToRevoke);
        blobUrlToRevoke = null;
      }
      
      // If it's a CORS error, show the CORS error message
      if (error.message && error.message.includes('tainted')) {
        handleCorsError();
      } else {
        // For other errors, try the fallback (annotations only)
        console.log('🔄 Attempting fallback: exporting annotations only...');
        try {
          const annotationsCanvas = document.createElement('canvas');
          annotationsCanvas.width = annotationCanvas.width;
          annotationsCanvas.height = annotationCanvas.height;
          const annotationsCtx = annotationsCanvas.getContext('2d');
          
          // Draw only the annotations (this shouldn't be tainted)
          annotationsCtx.drawImage(annotationCanvas, 0, 0);
          
          annotationsCanvas.toBlob((annotationsBlob) => {
            if (annotationsBlob && onSaveEdited) {
              console.log('✅ Annotations-only blob created, calling onSaveEdited...');
              try {
                onSaveEdited(annotationsBlob);
                setToastMessage('Annotations saved (original image could not be included due to CORS restrictions).');
                setToastType('warning');
                setShowToast(true);
                setTimeout(() => setShowToast(false), 5000);
              } catch (err) {
                console.error('❌ Error calling onSaveEdited with annotations:', err);
                handleCorsError();
              }
            } else {
              handleCorsError();
            }
          }, 'image/png');
        } catch (fallbackError) {
          console.error('❌ Fallback also failed:', fallbackError);
          handleCorsError();
        }
      }
    }
  };

  useEffect(() => {
    if (isDragging || isDrawing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleEditorTouchMove);
      document.addEventListener('touchend', handleEditorTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleEditorTouchMove);
        document.removeEventListener('touchend', handleEditorTouchEnd);
      };
    }
  }, [isDragging, isDrawing, dragStart, isEditMode, currentAnnotation, annotations]);

  useEffect(() => {
    if (isEditMode && annotations.length > 0) {
      redrawAnnotations();
    }
  }, [annotations]);

  // Ensure canvas is initialized when entering edit mode
  useEffect(() => {
    if (isEditMode && imageRef.current && canvasRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeCanvas();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isEditMode]);

  const renderToolButton = (tool, icon, tooltip) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{tooltip}</Tooltip>}
    >
      <Button
        variant={currentTool === tool ? 'primary' : 'outline-secondary'}
        size="sm"
        onClick={() => setCurrentTool(tool)}
      >
        {icon}
      </Button>
    </OverlayTrigger>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="zoomable-image-modal"
      dialogClassName="zoomable-image-modal-dialog"
    >
      <Modal.Header className="zoomable-image-modal-header">
        <Modal.Title className="d-flex align-items-center">
          <span className="me-2">Image Viewer</span>
          <span className="text-muted small">
            ({Math.round(scale * 100)}%)
            {isEditMode && <span className="ms-2 badge bg-warning text-dark">Edit Mode</span>}
          </span>
        </Modal.Title>
        <div className="d-flex gap-2 flex-wrap">
          {/* Viewer Controls - Always Visible */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleZoomOut}
            disabled={scale <= 0.1 || isEditMode}
            title="Zoom Out"
          >
            <ZoomOut size={16} />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleZoomIn}
            disabled={scale >= 5 || isEditMode}
            title="Zoom In"
          >
            <ZoomIn size={16} />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRotate}
            disabled={isEditMode}
            title="Rotate"
          >
            <RotateCw size={16} />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleReset}
            disabled={isEditMode}
            title="Reset"
          >
            <X size={16} />
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleDownload}
            title="Download"
          >
            <Download size={16} />
          </Button>

          {/* Vertical divider */}
          {enableEditor && <div className="vr" style={{ height: '32px' }}></div>}

          {/* Edit Mode Toggle */}
          {enableEditor && (
            <Button
              variant={isEditMode ? 'warning' : 'success'}
              size="sm"
              onClick={toggleEditMode}
              title={isEditMode ? 'Exit Edit Mode' : 'Edit Image'}
            >
              <Edit2 size={16} className="me-1" />
              {isEditMode ? 'Exit Edit' : 'Edit'}
            </Button>
          )}
        </div>
      </Modal.Header>

      {/* Editor Toolbar - Clean Modern Design */}
      {isEditMode && showEditorToolbar && (
        <div className="editor-toolbar-modern">
          <div className="toolbar-container">
            {/* Drawing Tools Section */}
            <div className="toolbar-section">
              <label className="section-title">Tools</label>
              <div className="tool-buttons">
                {renderToolButton('rectangle', <Square size={20} />, 'Rectangle')}
                {renderToolButton('circle', <Circle size={20} />, 'Circle')}
                {renderToolButton('line', <Minus size={20} />, 'Line')}
                {renderToolButton('arrow', <ArrowRight size={20} />, 'Arrow')}
                {renderToolButton('pen', <Edit2 size={20} />, 'Free Draw')}
                {renderToolButton('text', <Type size={20} />, 'Add Text')}
              </div>
            </div>

            <div className="toolbar-separator"></div>

            {/* Color & Size Section */}
            <div className="toolbar-section">
              <label className="section-title">Appearance</label>
              <div className="appearance-controls">
                <div className="color-control">
                  <label className="control-label">Color</label>
                  <input
                    type="color"
                    value={drawColor}
                    onChange={(e) => setDrawColor(e.target.value)}
                    className="modern-color-picker"
                  />
                </div>
                <div className="size-control">
                  <label className="control-label">Size: {strokeWidth}px</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={strokeWidth}
                    onChange={(e) => setStrokeWidth(Number(e.target.value))}
                    className="modern-slider"
                  />
                </div>
              </div>
            </div>

            <div className="toolbar-separator"></div>

            {/* Actions Section */}
            <div className="toolbar-section">
              <label className="section-title">Actions</label>
              <div className="action-buttons-group">
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleUndo}
                  disabled={historyStep < 0}
                  className="modern-action-btn"
                  title="Undo"
                >
                  <RotateCcw size={16} />
                  <span>Undo</span>
                </Button>

                <Button
                  variant="light"
                  size="sm"
                  onClick={handleClearAll}
                  disabled={annotations.length === 0}
                  className="modern-action-btn modern-action-btn-danger"
                  title="Clear All"
                >
                  <Trash2 size={16} />
                  <span>Clear</span>
                </Button>

                <Button
                  variant="success"
                  size="sm"
                  onClick={handleSaveEditedImage}
                  disabled={annotations.length === 0}
                  className="modern-save-btn"
                  title="Save Edited Image"
                >
                  <Save size={16} />
                  <span>Save</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal.Body
        className="zoomable-image-modal-body"
        onWheel={handleWheel}
        ref={containerRef}
      >
        <div className="zoomable-image-container">
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
              ref={imageRef}
              src={imageSrc}
              crossOrigin="anonymous"
              alt={imageAlt}
              className="zoomable-image"
              style={{
                transform: isEditMode
                  ? 'none'
                  : `scale(${scale}) rotate(${rotation}deg) translate(${position.x}px, ${position.y}px)`,
                cursor: isEditMode
                  ? (currentTool === 'select' ? 'default' : 'crosshair')
                  : (scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'),
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                display: 'block'
              }}
              onLoad={isEditMode ? initializeCanvas : undefined}
              onMouseDown={handleMouseDown}
              onDragStart={(e) => e.preventDefault()}
            />

            {isEditMode && (
              <canvas
                ref={canvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'auto',
                  touchAction: 'none'
                }}
                onMouseDown={handleEditorMouseDown}
                onMouseMove={handleEditorMouseMove}
                onMouseUp={handleEditorMouseUp}
                onTouchStart={handleEditorTouchStart}
                onTouchMove={handleEditorTouchMove}
                onTouchEnd={handleEditorTouchEnd}
              />
            )}

            {/* Inline Text Input Overlay */}
            {showTextInput && (
              <div
                className="text-input-overlay"
                style={{
                  position: 'absolute',
                  left: `${textInputPosition.x}px`,
                  top: `${textInputPosition.y}px`,
                  zIndex: 1000
                }}
              >
                <div className="text-input-container">
                  <input
                    ref={textInputRef}
                    type="text"
                    value={textInputValue}
                    onChange={(e) => setTextInputValue(e.target.value)}
                    onKeyDown={handleTextInputKeyDown}
                    placeholder="Type your text..."
                    className="text-annotation-input"
                    style={{
                      color: drawColor,
                      fontSize: `${strokeWidth * 4}px`,
                      fontWeight: 'bold'
                    }}
                  />
                  <div className="text-input-buttons">
                    <Button
                      size="sm"
                      variant="success"
                      onClick={handleTextInputSubmit}
                      disabled={!textInputValue.trim()}
                    >
                      ✓ Add
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleTextInputCancel}
                    >
                      ✕ Cancel
                    </Button>
                  </div>
                  <small className="text-input-hint">
                    Press Enter to add, Esc to cancel
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="zoomable-image-modal-footer">
        <div className="d-flex justify-content-between align-items-center w-100">
          <small className="text-muted">
            {isEditMode
              ? 'Select a tool and draw on the image • Click "Save" to download edited image'
              : 'Use mouse wheel to zoom • Click and drag to pan when zoomed'
            }
          </small>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </div>
      </Modal.Footer>

      {/* Custom Confirmation Modal for Clear All */}
      {showClearConfirm && (
        <div className="custom-confirm-overlay">
          <div className="custom-confirm-modal">
            <div className="confirm-icon">
              <Trash2 size={48} color="#f44336" />
            </div>
            <h4 className="confirm-title">Clear All Annotations?</h4>
            <p className="confirm-message">
              This action will remove all drawings and text from the image. This cannot be undone.
            </p>
            <div className="confirm-buttons">
              <Button
                variant="light"
                size="lg"
                onClick={handleCancelClear}
                className="confirm-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="lg"
                onClick={handleConfirmClear}
                className="confirm-delete-btn"
              >
                Yes, Clear All
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Toast Notification */}
      {showToast && (
        <div className="custom-toast-overlay">
          <div className={`custom-toast ${toastType === 'success' ? 'toast-success' : 'toast-error'}`}>
            <div className="toast-icon">
              {toastType === 'success' ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              )}
            </div>
            <div className="toast-content">
              <p className="toast-message">{toastMessage}</p>
            </div>
            <button
              className="toast-close-btn"
              onClick={() => setShowToast(false)}
              aria-label="Close notification"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ZoomableImageModal;
